import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { BellRing, Search, Mail, MessageSquare, Webhook, ArrowRight, Sparkles, Pause } from "lucide-react";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ALERTS } from "@/constants/testIds";
import { methodLabel, describeConfig, SEVERITY } from "@/lib/alerts";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const CHANNEL_ICON = { email: Mail, slack: MessageSquare, webhook: Webhook };

function rel(ts) {
  if (!ts) return "—";
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return "—"; }
}

function AlertRow({ a }) {
  const Icon = CHANNEL_ICON[a.delivery] || Webhook;
  const lastSev = a.last_breach?.severity;
  const sev = SEVERITY[lastSev];
  return (
    <Link to={`/alerts/${a.id}`} data-testid={ALERTS.row}
      className="group grid grid-cols-12 items-center gap-3 border-b border-border/40 px-4 py-4 transition-colors last:border-0 hover:bg-muted/30">
      <div className="col-span-12 sm:col-span-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <BellRing className="h-4 w-4 shrink-0 text-accent" />
          <span className="truncate">{a.name}</span>
          {!a.active && <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/40"><Pause className="mr-1 h-2.5 w-2.5" />paused</Badge>}
        </div>
        <p className="mt-1 truncate pl-6 text-xs text-muted-foreground">{describeConfig(a.method, a.config)}</p>
      </div>

      <div className="col-span-6 sm:col-span-2">
        <Badge variant="outline" className="text-[10px] text-accent border-accent/30">{methodLabel(a.method)}</Badge>
      </div>

      <div className="col-span-6 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Last breach</div>
        {sev ? (
          <div className="mt-0.5 flex items-center gap-1.5 text-xs">
            <span className={`h-2 w-2 rounded-full ${sev.dot}`} />
            <span>{sev.label}</span>
            <span className="text-muted-foreground">{rel(a.last_breach?.ran_at)}</span>
          </div>
        ) : <div className="mt-0.5 text-xs text-muted-foreground">none</div>}
      </div>

      <div className="col-span-6 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Last run</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{rel(a.last_run?.ran_at)}</div>
      </div>

      <div className="col-span-5 sm:col-span-1">
        <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-card/50 px-2 py-0.5 text-[10px] text-muted-foreground">
          <Icon className="h-3 w-3 text-accent" /> {a.delivery}
        </span>
      </div>

      <div className="col-span-1 hidden justify-end sm:flex">
        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
      </div>
    </Link>
  );
}

export default function Alerts() {
  const { active } = useWorkspace();
  const [alerts, setAlerts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.get("/alerts", { params: { workspace_id: active.id } });
      setAlerts(data);
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return alerts;
    return alerts.filter((a) => `${a.name} ${a.prompt} ${a.method}`.toLowerCase().includes(t));
  }, [alerts, q]);

  return (
    <div data-testid={ALERTS.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-7xl px-6 py-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Alerts</h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Stop watching dashboards. Sema pings you the moment a metric deviates beyond noise.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid={ALERTS.search} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search alerts…" className="bg-card/60 pl-9" />
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-border/60 bg-card/30">
          {loading ? (
            <div className="px-4 py-16 text-center text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length ? (
            filtered.map((a) => <AlertRow key={a.id} a={a} />)
          ) : (
            <div data-testid={ALERTS.empty} className="px-4 py-20 text-center">
              <Sparkles className="mx-auto h-8 w-8 text-accent" />
              <h2 className="mt-4 text-lg font-semibold">No alerts yet</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Ask a question in <Link to="/chat" className="text-accent hover:underline">Ask</Link>, then choose
                “Watch this metric”.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
